import React from "react";
import { toast } from "sonner";
import { nanoid } from "nanoid";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { fileService } from "@/services/file-service";
import {
  useBookingCheckoutSettings,
  useCreateBookingCheckoutSettings,
  useUpdateBookingCheckoutSettings,
} from "@/components/pages/checkout-settings/hooks/useBookingCheckoutSettings";
import { useHotel } from "@/hooks/useHotel";
import type {
  CheckoutSettingsResponse,
  PostCheckoutFeedbackSettings,
} from "@/types/booking-checkout-settings";
import {
  FeedbackSettingsTab,
  FeedbackSettingsTabs,
} from "./components/FeedbackSettingsTabs";
import { PostCheckoutFeedbackPreview } from "./components/PostCheckoutFeedbackPreview";
import { ReservationFeedbackSettingsForm } from "./components/ReservationFeedbackSettingsForm";
import { RequestFeedbackSettingsForm } from "./components/RequestFeedbackSettingsForm";
import { CommunicationFeedbackSettingsForm } from "./components/CommunicationFeedbackSettingsForm";
import {
  useCommunicationFeedbackSettings,
  useRequestFeedbackSettings,
  useUpdateCommunicationFeedbackSettings,
  useUpdateRequestFeedbackSettings,
} from "./hooks/useFeedbackSettings";
import {
  CommunicationFeedbackSettings,
  RequestFeedbackSettings,
} from "@/types/feedback-settings";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  ArrowLeft,
  Bell,
  Ellipsis,
  MessageSquareMore,
  Mic,
  Paperclip,
  Signal,
  Star,
  Wifi,
  BatteryFull,
} from "lucide-react";

const defaultPostCheckoutFeedback: PostCheckoutFeedbackSettings = {
  enabled: true,
  initialSendAfterHours: 12,
  mediaId: null,
  channels: ["WHATSAPP", "APP", "EMAIL"],
  externalReviewsEnabled: true,
  bookingReviewUrl: "",
  googleReviewUrl: "",
  lowRatingThreshold: 4,
  reminders: [
    {
      clientId: nanoid(),
      order: 1,
      sendAfterHours: 6,
      condition: "EMAIL_NOT_OPENED",
      isActive: true,
    },
  ],
};

const buildCreatePayload = (
  postCheckoutFeedback: PostCheckoutFeedbackSettings,
  reviewCategories: string[],
) => ({
  isLeaveReview: true,
  isLeaveComment: true,
  isBillSummary: true,
  isSkipBill: true,
  isUpsellRecommendedServices: true,
  reviewCategories,
  postCheckoutFeedback,
});

const resolveInitialFeedbackSettings = (
  settings?: CheckoutSettingsResponse,
): PostCheckoutFeedbackSettings => {
  const reminders = settings?.postCheckoutFeedback?.reminders?.length
    ? settings.postCheckoutFeedback.reminders
    : defaultPostCheckoutFeedback.reminders;

  return {
    ...defaultPostCheckoutFeedback,
    ...settings?.postCheckoutFeedback,
    channels:
      settings?.postCheckoutFeedback?.channels ??
      defaultPostCheckoutFeedback.channels,
    reminders: reminders.map((reminder, index) => ({
      ...reminder,
      clientId: reminder.clientId ?? reminder.id ?? nanoid(),
      order: index + 1,
    })),
  };
};

const defaultRequestFeedbackSettings: RequestFeedbackSettings = {
  enabled: false,
  allowComment: false,
};

const defaultCommunicationFeedbackSettings: CommunicationFeedbackSettings = {
  enabled: false,
  question: "Please rate the communication",
  responseType: "STARS",
  allowComment: false,
  followUps: [],
};

const resolveInitialRequestFeedbackSettings = (
  settings?: RequestFeedbackSettings,
): RequestFeedbackSettings => ({
  ...defaultRequestFeedbackSettings,
  ...settings,
});

const resolveInitialCommunicationFeedbackSettings = (
  settings?: CommunicationFeedbackSettings,
): CommunicationFeedbackSettings => ({
  ...defaultCommunicationFeedbackSettings,
  ...settings,
  followUps:
    settings?.followUps?.map((followUp, index) => ({
      ...followUp,
      clientId: followUp.clientId ?? followUp.id ?? nanoid(),
      order: index + 1,
    })) ?? defaultCommunicationFeedbackSettings.followUps,
});

export const FeedbackSettingsPage = (): React.JSX.Element => {
  const { t } = useTranslation();
  const { hotelId } = useHotel();
  const [activeTab, setActiveTab] =
    useState<FeedbackSettingsTab>("reservations");
  const [settings, setSettings] = useState<PostCheckoutFeedbackSettings>(
    defaultPostCheckoutFeedback,
  );
  const [requestSettings, setRequestSettings] =
    useState<RequestFeedbackSettings>(defaultRequestFeedbackSettings);
  const [communicationSettings, setCommunicationSettings] =
    useState<CommunicationFeedbackSettings>(
      defaultCommunicationFeedbackSettings,
    );
  const [isUploading, setIsUploading] = useState(false);

  const checkoutSettingsQuery = useBookingCheckoutSettings(hotelId);
  const requestSettingsQuery = useRequestFeedbackSettings(hotelId);
  const communicationSettingsQuery = useCommunicationFeedbackSettings(hotelId);
  const updateMutation = useUpdateBookingCheckoutSettings();
  const createMutation = useCreateBookingCheckoutSettings();
  const updateRequestMutation = useUpdateRequestFeedbackSettings();
  const updateCommunicationMutation = useUpdateCommunicationFeedbackSettings();

  useEffect(() => {
    if (!checkoutSettingsQuery.data) return;
    setSettings(resolveInitialFeedbackSettings(checkoutSettingsQuery.data));
  }, [checkoutSettingsQuery.data]);

  useEffect(() => {
    if (!requestSettingsQuery.data) return;
    setRequestSettings(
      resolveInitialRequestFeedbackSettings(requestSettingsQuery.data),
    );
  }, [requestSettingsQuery.data]);

  useEffect(() => {
    if (!communicationSettingsQuery.data) return;
    setCommunicationSettings(
      resolveInitialCommunicationFeedbackSettings(
        communicationSettingsQuery.data,
      ),
    );
  }, [communicationSettingsQuery.data]);

  const handleMediaUpload = async (file: File): Promise<void> => {
    try {
      setIsUploading(true);
      const mediaId = await fileService.uploadFileWithPresignedUrl(file);
      setSettings((currentSettings) => ({
        ...currentSettings,
        mediaId,
      }));
    } catch (error) {
      console.error("Failed to upload feedback media:", error);
      toast.error(t("feedbacks.settings.messages.uploadError"));
    } finally {
      setIsUploading(false);
    }
  };

  const handleReservationSave = (): void => {
    const reminders = settings.reminders.map((reminder) => ({
      order: reminder.order,
      sendAfterHours: reminder.sendAfterHours,
      condition: reminder.condition,
      isActive: reminder.isActive,
    }));

    const payload = {
      ...settings,
      reminders,
      bookingReviewUrl: settings.bookingReviewUrl?.trim() || null,
      googleReviewUrl: settings.googleReviewUrl?.trim() || null,
    };

    const mutationOptions = {
      onSuccess: () => toast.success(t("feedbacks.settings.messages.saved")),
      onError: () => toast.error(t("feedbacks.settings.messages.saveError")),
    };

    if (checkoutSettingsQuery.data) {
      updateMutation.mutate(
        {
          postCheckoutFeedback: payload,
        },
        mutationOptions,
      );
      return;
    }

    createMutation.mutate(
      buildCreatePayload(payload, [
        t("feedbacks.settings.preview.categories.purity"),
        t("feedbacks.settings.preview.categories.comfort"),
        t("feedbacks.settings.preview.categories.service"),
      ]),
      mutationOptions,
    );
  };

  const handleSave = (): void => {
    if (activeTab === "requests") {
      updateRequestMutation.mutate(requestSettings, {
        onSuccess: () => toast.success(t("feedbacks.settings.messages.saved")),
        onError: () => toast.error(t("feedbacks.settings.messages.saveError")),
      });
      return;
    }

    if (activeTab === "communication") {
      updateCommunicationMutation.mutate(
        {
          enabled: communicationSettings.enabled,
          question:
            communicationSettings.question.trim() ||
            t("feedbacks.settings.communication.questionPlaceholder"),
          responseType: communicationSettings.responseType,
          allowComment: communicationSettings.allowComment,
          followUps: communicationSettings.followUps.map((followUp) => ({
            title: followUp.title,
            order: followUp.order,
            isActive: followUp.isActive,
            minRating: Math.min(followUp.minRating, followUp.maxRating),
            maxRating: Math.max(followUp.minRating, followUp.maxRating),
            actionType: followUp.actionType,
            messageTemplate: followUp.messageTemplate,
          })),
        },
        {
          onSuccess: () =>
            toast.success(t("feedbacks.settings.messages.saved")),
          onError: () =>
            toast.error(t("feedbacks.settings.messages.saveError")),
        },
      );
      return;
    }

    handleReservationSave();
  };

  const handleReset = () => {
    if (activeTab === "requests") {
      setRequestSettings(
        resolveInitialRequestFeedbackSettings(requestSettingsQuery.data),
      );
      return;
    }

    if (activeTab === "communication") {
      setCommunicationSettings(
        resolveInitialCommunicationFeedbackSettings(
          communicationSettingsQuery.data,
        ),
      );
      return;
    }

    setSettings(resolveInitialFeedbackSettings(checkoutSettingsQuery.data));
  };

  const isSaving =
    (activeTab === "reservations" &&
      (updateMutation.isPending || createMutation.isPending)) ||
    (activeTab === "requests" && updateRequestMutation.isPending) ||
    (activeTab === "communication" && updateCommunicationMutation.isPending);

  const isLoading =
    (activeTab === "reservations" && checkoutSettingsQuery.isLoading) ||
    (activeTab === "requests" && requestSettingsQuery.isLoading) ||
    (activeTab === "communication" && communicationSettingsQuery.isLoading);

  if (isLoading) {
    return <div className="p-8">{t("feedbacks.loading")}</div>;
  }

  return (
    <div className="bg-background flex h-full min-h-0 flex-col overflow-hidden">
      <div className="border-border border-b px-6 py-5">
        <FeedbackSettingsTabs
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden lg:flex-row">
        <main className="min-h-0 flex-1 overflow-y-auto px-6 py-6">
          <div className="mx-auto flex max-w-4xl flex-col gap-5">
            {activeTab === "reservations" ? (
              <ReservationFeedbackSettingsForm
                settings={settings}
                isUploading={isUploading}
                onSettingsChange={setSettings}
                onMediaUpload={(file) => {
                  void handleMediaUpload(file);
                }}
              />
            ) : null}

            {activeTab === "requests" ? (
              <RequestFeedbackSettingsForm
                settings={requestSettings}
                onSettingsChange={setRequestSettings}
              />
            ) : null}

            {activeTab === "communication" ? (
              <CommunicationFeedbackSettingsForm
                settings={communicationSettings}
                onSettingsChange={setCommunicationSettings}
              />
            ) : null}

            <div className="flex justify-end gap-3 pb-6">
              <Button type="button" variant="outline" onClick={handleReset}>
                {t("feedbacks.settings.actions.cancel")}
              </Button>
              <Button type="button" disabled={isSaving} onClick={handleSave}>
                {isSaving
                  ? t("feedbacks.settings.actions.saving")
                  : t("feedbacks.settings.actions.saveChanges")}
              </Button>
            </div>
          </div>
        </main>

        {activeTab === "reservations" ? (
          <PostCheckoutFeedbackPreview settings={settings} />
        ) : (
          <FeedbackSettingsSidePreview
            activeTab={activeTab}
            requestSettings={requestSettings}
            communicationSettings={communicationSettings}
          />
        )}
      </div>
    </div>
  );
};

interface FeedbackSettingsSidePreviewProps {
  activeTab: FeedbackSettingsTab;
  requestSettings: RequestFeedbackSettings;
  communicationSettings: CommunicationFeedbackSettings;
}

const FeedbackSettingsSidePreview = ({
  activeTab,
  requestSettings,
  communicationSettings,
}: FeedbackSettingsSidePreviewProps): React.JSX.Element => {
  const { t } = useTranslation();

  const communicationQuestion =
    communicationSettings.question.trim() ||
    t("feedbacks.settings.communication.preview.title");

  const renderCommunicationResponseControl = (): React.JSX.Element => {
    if (communicationSettings.responseType === "TEXT") {
      return (
        <Textarea
          readOnly
          value={t("feedbacks.settings.communication.preview.textAnswer")}
          className="border-border/70 bg-background text-muted-foreground mt-4 min-h-[78px] resize-none rounded-2xl px-3 py-2 text-[11px] leading-4 shadow-sm"
        />
      );
    }

    if (communicationSettings.responseType === "RATING") {
      return (
        <div className="mt-4 flex justify-center gap-2">
          {Array.from({ length: 5 }).map((_, index) => (
            <div
              key={index}
              className="border-border bg-background text-eathy-comfort-1 flex size-8 items-center justify-center rounded-full border text-[12px] font-semibold shadow-sm"
            >
              {index + 1}
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="mt-4 flex justify-center gap-3">
        {Array.from({ length: 5 }).map((_, index) => (
          <Star
            key={index}
            className="text-muted-foreground/22 size-5 fill-transparent stroke-[1.8]"
          />
        ))}
      </div>
    );
  };

  return (
    <aside className="bg-muted/30 flex min-h-full w-full flex-col items-center p-5 lg:max-w-md">
      <div className="sticky top-0 flex flex-col items-center">
        <div className="text-center">
          <h3 className="text-lg font-semibold">
            {t("feedbacks.settings.preview.title")}
          </h3>
        </div>

        <div className="mt-2 w-[320px]">
          {activeTab === "requests" ? (
            <div className="bg-eathy-comfort-5 overflow-hidden rounded-[36px] border-[6px] border-black shadow-sm">
              <div className="text-foreground relative flex h-10 items-center justify-between px-6 text-xs font-semibold">
                <span>9:41</span>
                <div className="absolute top-1/2 left-1/2 h-5 w-20 -translate-x-1/2 -translate-y-1/2 rounded-full bg-black" />
                <div className="flex items-center gap-1">
                  <Signal className="size-3.5" />
                  <Wifi className="size-3.5" />
                  <BatteryFull className="size-4" />
                </div>
              </div>

              <div className="bg-eathy-comfort-5 flex h-[620px] flex-col overflow-hidden px-5 pb-4">
                <div className="flex items-center justify-between pt-2">
                  <div className="bg-background/80 flex size-9 items-center justify-center rounded-full shadow-sm">
                    <ArrowLeft className="size-4" />
                  </div>
                  <h4 className="text-eathy-comfort-1 text-base font-semibold">
                    {t("feedbacks.settings.requests.preview.screenTitle")}
                  </h4>
                  <div className="w-9" />
                </div>

                <div
                  className={
                    requestSettings.enabled
                      ? "from-eathy-comfort-4/60 via-background to-eathy-comfort-4/40 relative mt-4 h-[150px] overflow-hidden rounded-[22px] bg-gradient-to-br shadow-inner"
                      : "bg-muted relative mt-4 h-[150px] overflow-hidden rounded-[22px] opacity-60 shadow-inner"
                  }
                >
                  <div className="to-eathy-comfort-1/10 absolute inset-0 bg-gradient-to-b from-transparent via-transparent" />
                  <div className="bg-background/40 absolute top-8 -left-6 size-20 rounded-full blur-xl" />
                  <div className="bg-background/60 absolute top-5 right-3 h-24 w-24 rounded-full blur-2xl" />
                  <div className="border-background/50 bg-background/35 absolute top-4 left-1/2 h-28 w-36 -translate-x-1/2 rounded-[28px] border" />
                  <div className="bg-background/35 absolute bottom-4 left-4 h-10 w-24 rounded-full" />
                  <div className="bg-background/30 absolute right-5 bottom-5 h-8 w-20 rounded-full" />
                </div>

                <Card
                  className={
                    requestSettings.enabled
                      ? "border-border/60 bg-background relative -mt-9 gap-0 rounded-[28px] px-4 pt-5 pb-4 shadow-sm"
                      : "border-border/60 bg-background relative -mt-9 gap-0 rounded-[28px] px-4 pt-5 pb-4 opacity-60 shadow-sm"
                  }
                >
                  <CardContent className="flex flex-col items-center gap-4 px-0">
                    <div className="border-border bg-background text-muted-foreground flex size-11 items-center justify-center rounded-2xl border shadow-xs">
                      <MessageSquareMore className="size-4.5" />
                    </div>

                    <div className="text-eathy-comfort-1 text-center text-[15px] font-semibold">
                      {t("feedbacks.settings.requests.preview.title")}
                    </div>

                    <div className="flex gap-2.5">
                      {Array.from({ length: 5 }).map((_, index) => (
                        <Star
                          key={index}
                          className={
                            index < 4
                              ? "size-5 fill-yellow-400 text-yellow-400"
                              : "fill-muted text-muted-foreground/35 size-5"
                          }
                        />
                      ))}
                    </div>

                    {requestSettings.allowComment ? (
                      <div className="w-full space-y-1.5">
                        <div className="text-eathy-comfort-1 text-[11px] font-semibold">
                          {t(
                            "feedbacks.settings.requests.preview.commentsLabel",
                          )}
                        </div>
                        <Textarea
                          value={t(
                            "feedbacks.settings.requests.preview.commentPlaceholder",
                          )}
                          readOnly
                          className="border-border/70 bg-background min-h-[84px] resize-none rounded-2xl px-3 py-2 text-[11px] leading-4"
                        />
                      </div>
                    ) : null}

                    <Button
                      type="button"
                      variant="outline"
                      className="border-border/70 bg-background text-eathy-comfort-1 hover:bg-background h-9 w-full rounded-full text-sm font-normal shadow-none"
                    >
                      {t("feedbacks.settings.actions.cancel")}
                    </Button>
                    <Button
                      type="button"
                      className="bg-royal-glow-1 hover:bg-royal-glow-1/90 h-9 w-full rounded-full text-sm text-white"
                    >
                      {t("feedbacks.settings.requests.preview.confirm")}
                    </Button>
                  </CardContent>
                </Card>

                <div className="mt-auto pt-4">
                  <Button
                    type="button"
                    className="bg-royal-glow-1 hover:bg-royal-glow-1/90 h-11 w-full rounded-full text-sm text-white"
                    disabled={!requestSettings.enabled}
                  >
                    {t("feedbacks.settings.requests.preview.cta")}
                  </Button>
                  <div className="mx-auto mt-3 h-1 w-24 rounded-full bg-black" />
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-eathy-comfort-5 overflow-hidden rounded-[36px] border-[6px] border-black shadow-sm">
              <div className="text-foreground relative flex h-10 items-center justify-between px-6 text-xs font-semibold">
                <span>9:41</span>
                <div className="absolute top-1/2 left-1/2 h-5 w-20 -translate-x-1/2 -translate-y-1/2 rounded-full bg-black" />
                <div className="flex items-center gap-1">
                  <Signal className="size-3.5" />
                  <Wifi className="size-3.5" />
                  <BatteryFull className="size-4" />
                </div>
              </div>

              <div className="bg-eathy-comfort-5 flex h-[620px] flex-col overflow-hidden">
                <div className="px-4 pt-2 pb-3">
                  <div className="flex items-center justify-between">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon_sm"
                      className="text-eathy-comfort-1 size-8 rounded-full hover:bg-transparent"
                    >
                      <ArrowLeft className="size-4" />
                    </Button>
                    <h4 className="text-eathy-comfort-1 text-[15px] font-semibold">
                      {t(
                        "feedbacks.settings.communication.preview.headerTitle",
                      )}
                    </h4>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon_sm"
                      className="text-eathy-comfort-1 relative size-8 rounded-full hover:bg-transparent"
                    >
                      <Bell className="size-4" />
                      <span className="bg-royal-glow-2 absolute -top-1 -right-1 flex size-4 items-center justify-center rounded-full text-[9px] font-semibold text-white">
                        2
                      </span>
                    </Button>
                  </div>
                </div>

                <div className="bg-muted/25 flex flex-1 flex-col px-4 pt-7 pb-4">
                  <Badge
                    variant="outline"
                    className="border-border/60 bg-background text-muted-foreground mx-auto rounded-full px-3 py-1 text-[10px] font-medium shadow-sm"
                  >
                    {t("feedbacks.settings.communication.preview.today")}
                  </Badge>

                  <Card className="bg-skyline-1/35 mt-5 gap-0 rounded-[1.3rem] border-0 p-1.5 shadow-none">
                    <CardContent className="bg-background flex gap-2 rounded-[0.95rem] p-1.5">
                      <div className="h-[72px] w-[72px] shrink-0 rounded-[0.75rem] bg-[linear-gradient(135deg,#4c422f_0%,#b08a56_45%,#f0e4d0_100%)]" />
                      <div className="flex min-w-0 flex-1 flex-col">
                        <div className="flex items-center justify-between gap-2">
                          <Badge
                            variant="outline"
                            className="border-skyline-2/35 bg-skyline-1/20 text-skyline-2 rounded-full px-2 py-0.5 text-[9px] font-medium"
                          >
                            {t(
                              "feedbacks.settings.communication.preview.serviceStatus",
                            )}
                          </Badge>
                          <span className="text-royal-glow-2 text-[11px] font-semibold">
                            {t(
                              "feedbacks.settings.communication.preview.servicePrice",
                            )}
                          </span>
                        </div>
                        <div className="text-eathy-comfort-1 mt-1 text-[10px] leading-4 font-semibold">
                          {t(
                            "feedbacks.settings.communication.preview.serviceTitle",
                          )}
                        </div>
                        <div className="text-eathy-comfort-1/75 mt-1 line-clamp-2 text-[10px] leading-3">
                          {t(
                            "feedbacks.settings.communication.preview.serviceDescription",
                          )}
                        </div>
                        <div className="mt-auto flex items-center justify-between gap-2 pt-1">
                          <span className="text-eathy-comfort-1/70 text-[9px]">
                            {t(
                              "feedbacks.settings.communication.preview.serviceTime",
                            )}
                          </span>
                          <Badge className="bg-royal-glow-1 rounded-full px-3 py-1 text-[9px] font-medium text-white">
                            {t(
                              "feedbacks.settings.communication.preview.serviceAction",
                            )}
                          </Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <div className="text-muted-foreground mt-1 flex justify-end pr-1 text-[10px]">
                    <span>
                      {t(
                        "feedbacks.settings.communication.preview.messageTime",
                      )}
                    </span>
                  </div>

                  <div className="mt-7 flex items-end gap-2">
                    <Avatar className="bg-royal-glow-1 mb-2 size-8 shadow-sm">
                      <AvatarFallback className="bg-royal-glow-1 text-white">
                        <MessageSquareMore className="size-4" />
                      </AvatarFallback>
                    </Avatar>
                    <Card className="border-border/70 bg-background max-w-[82%] gap-0 rounded-[1.35rem] rounded-bl-[0.9rem] px-4 py-3 shadow-sm">
                      <CardContent className="text-eathy-comfort-1 px-0 text-sm leading-6">
                        <div className="text-muted-foreground mb-1 text-[10px] font-medium">
                          {t(
                            "feedbacks.settings.communication.preview.headerTitle",
                          )}
                        </div>
                        {t(
                          "feedbacks.settings.communication.preview.chatBubble",
                        )}
                      </CardContent>
                    </Card>
                  </div>

                  <div className="text-muted-foreground mt-1 flex items-center justify-end gap-2 pr-1 text-[10px]">
                    <Badge
                      variant="outline"
                      className="border-royal-glow-5 bg-background text-royal-glow-2 rounded-full px-2 py-0.5 shadow-xs"
                    >
                      {t(
                        "feedbacks.settings.communication.preview.seeOriginal",
                      )}
                    </Badge>
                    <span>
                      {t(
                        "feedbacks.settings.communication.preview.messageTime",
                      )}
                    </span>
                  </div>

                  <div className="text-eathy-comfort-1 mt-8 text-center text-[13px] font-medium">
                    {communicationQuestion}
                  </div>

                  {renderCommunicationResponseControl()}

                  {communicationSettings.allowComment ? (
                    <div className="mt-4 flex justify-center">
                      <Button
                        type="button"
                        variant="outline"
                        className="border-border bg-background text-eathy-comfort-1 inline-flex items-center gap-2 rounded-full border px-3 py-2 text-[12px] font-medium shadow-sm"
                      >
                        <MessageSquareMore className="text-muted-foreground size-3.5" />
                        {t(
                          "feedbacks.settings.communication.preview.addCommentCta",
                        )}
                      </Button>
                    </div>
                  ) : null}

                  <div className="bg-background ring-border/60 mt-auto flex items-center gap-2 rounded-full px-3 py-2 shadow-sm ring-1">
                    <Button
                      type="button"
                      size="icon_sm"
                      className="bg-royal-glow-1 hover:bg-royal-glow-1/90 size-8 rounded-full text-white"
                    >
                      <Ellipsis className="size-4" />
                    </Button>
                    <Input
                      readOnly
                      value={t(
                        "feedbacks.settings.communication.preview.inputPlaceholder",
                      )}
                      className="border-border bg-background text-muted-foreground h-9 flex-1 rounded-full px-3 text-xs shadow-none"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon_sm"
                      className="text-muted-foreground size-8 rounded-full hover:bg-transparent"
                    >
                      <Paperclip className="size-4" />
                    </Button>
                    <Button
                      type="button"
                      size="icon_sm"
                      className="size-8 rounded-full bg-black text-white hover:bg-black/90"
                    >
                      <Mic className="size-4" />
                    </Button>
                  </div>
                </div>

                <div className="mx-auto mt-auto h-1 w-24 rounded-full bg-black" />
              </div>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
};
